import React, { useContext, useState } from "react";
import { Layout, Menu, Dropdown, Drawer, Button, Grid } from "antd";
import {
  DashboardOutlined,
  TeamOutlined,
  ApartmentOutlined,
  SafetyCertificateOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  ScheduleOutlined,
  HistoryOutlined,
  LogoutOutlined,
  CalendarOutlined,
  FileTextOutlined,
  FileProtectOutlined,
  HomeOutlined,
  DollarOutlined,
  GiftOutlined,
  FileExcelOutlined,
  AuditOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import NotificationBell from "./NotificationBell";
import ProfileAvatar from "./ProfileAvatar";

const { Header, Sider, Content } = Layout;

const menuItems = [
  { key: "/dashboard", icon: <DashboardOutlined />, label: "Dashboard" },
  { key: "/employees", icon: <TeamOutlined />, label: "Employees" },
  { key: "/departments", icon: <ApartmentOutlined />, label: "Departments" },
  { key: "/roles", icon: <SafetyCertificateOutlined />, label: "Roles" },
  { key: "/offices", icon: <EnvironmentOutlined />, label: "Offices" },
  { key: "/working-hours", icon: <ClockCircleOutlined />, label: "Working Hours" },
  { key: "/attendance", icon: <ScheduleOutlined />, label: "Attendance" },
  { key: "/activity-history", icon: <HistoryOutlined />, label: "Activity History" },
  { key: "/holidays", icon: <CalendarOutlined />, label: "Holidays" },
  { key: "/policies", icon: <FileProtectOutlined />, label: "Policies" },
  { key: "/leave", icon: <FileTextOutlined />, label: "Leave" },
  { key: "/wfh-requests", icon: <HomeOutlined />, label: "WFH Requests" },
  { key: "/pay-run", icon: <DollarOutlined />, label: "Pay Run" },
  { key: "/reimbursements", icon: <DollarOutlined />, label: "Reimbursements" },
  { key: "/overtime-requests", icon: <ClockCircleOutlined />, label: "Overtime Requests" },
  { key: "/benefits", icon: <GiftOutlined />, label: "Benefits" },
  { key: "/reports", icon: <FileExcelOutlined />, label: "Reports" },
  { key: "/audit-log", icon: <AuditOutlined />, label: "Audit Log" },
];

const LogoMark = () => (
  <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="26" height="26" rx="7" fill="var(--i-ems-accent)" />
    <path d="M7.5 13.5l3.5 3.5 7.5-8" stroke="#0B1330" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const { useBreakpoint } = Grid;

const Main = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, logout } = useContext(AuthContext);
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const userMenu = (
    <Menu
      items={[{ key: "logout", icon: <LogoutOutlined />, label: "Logout", onClick: logout }]}
    />
  );

  const navMenu = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[(menuItems.find((m) => location.pathname === m.key || location.pathname.startsWith(m.key + "/")) || {}).key || location.pathname]}
      items={menuItems}
      onClick={({ key }) => {
        navigate(key);
        setDrawerOpen(false);
      }}
    />
  );

  return (
    <Layout className="i-ems-shell" style={{ minHeight: "100%" }}>
      {/* Desktop/tablet: a normal fixed sidebar, unchanged from before.
          Mobile: no Sider at all — the built-in antd "zero-width trigger"
          it would otherwise auto-render overlaps page content with no
          backdrop and no real animation, so a proper Drawer (below)
          replaces it entirely on small screens. */}
      {!isMobile && (
        <Sider width={240} theme="dark" className="i-ems-sider">
          <div className="i-ems-logo">
            <LogoMark />
            <span>I-EMS Admin</span>
          </div>
          {navMenu}
        </Sider>
      )}

      <Drawer
        placement="left"
        closable={false}
        onClose={() => setDrawerOpen(false)}
        open={isMobile && drawerOpen}
        width={240}
        bodyStyle={{ padding: 0, background: "linear-gradient(180deg, #1e1b4b 0%, #312e81 100%)" }}
        className="i-ems-mobile-drawer"
      >
        <div className="i-ems-logo">
          <LogoMark />
          <span>I-EMS Admin</span>
        </div>
        {navMenu}
      </Drawer>

      <Layout className="i-ems-main">
        <Header className="i-ems-topbar">
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setDrawerOpen(true)}
              className="i-ems-mobile-menu-trigger"
              aria-label="Open navigation menu"
            />
          )}
          <div className="i-ems-topbar-actions">
            <NotificationBell />
            <Dropdown overlay={userMenu} placement="bottomRight" trigger={["click"]}>
              <div className="i-ems-user-trigger">
                <ProfileAvatar />
                <span>{userProfile?.name || "Admin"}</span>
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content className="i-ems-content">{children}</Content>
      </Layout>
    </Layout>
  );
};

export default Main;
